import React, { useEffect, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { useAuth } from '../../contexts/AuthContext';
import { classesAPI } from '../../services/classService';
import { employeesAPI } from '../../services/employeesService';
import { subjectsAPI } from '../../services/subjectsService';
import ClassesTable from './ClassesTable';
import ClassDialog from './ClassDialog';
import SectionDialog from './SectionDialog';
import ClassFilter from './ClassFilter';

const Classes = () => {
  const { user } = useAuth();
  const toast = useRef(null);

  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [expandedRows, setExpandedRows] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 4,
    page: 1,
    filters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    }
  });

  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [selectedClassFilter, setSelectedClassFilter] = useState(null);

  // Dialog states
  const [classDialogVisible, setClassDialogVisible] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [classForm, setClassForm] = useState({ name: '', description: '' });
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [dialogClassNameValue, setDialogClassNameValue] = useState('');
  const [commonClasses, setCommonClasses] = useState([]);
  const [commonSections, setCommonSections] = useState([]);

  // Section management state
  const [sectionDialogVisible, setSectionDialogVisible] = useState(false);
  const [selectedClassForSection, setSelectedClassForSection] = useState(null);
  const [selectedSectionNames, setSelectedSectionNames] = useState([]); // sections to assign
  const [sectionTeacherMap, setSectionTeacherMap] = useState({}); // { sectionName: teacherId }
  const [availableTeachers, setAvailableTeachers] = useState([]);

  // view state removed — simplified table-only mode

  useEffect(() => {
    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyParams, selectedClassFilter, globalFilterValue]);

  const loadClasses = async () => {
    setLoading(true);
    try {
      const params = {
        page: lazyParams.page || 1,
        limit: lazyParams.rows || 10,
        search: globalFilterValue || lazyParams.filters?.global?.value || '',
      };

      const res = await classesAPI.getclasseswithparams(params);
      // tolerant response parsing like original
      let data = [];
      let total = 0;
      if (res?.data?.success) {
        if (Array.isArray(res.data.data)) {
          data = res.data.data;
          total = res.data.pagination?.total || res.data.pagination?.totalRows || data.length;
        } else if (res.data.data?.classes) {
          data = res.data.data.classes;
          total = res.data.data.pagination?.total || res.data.data.pagination?.totalRows || data.length;
        } else if (Array.isArray(res.data)) {
          data = res.data;
          total = data.length;
        }
      }
      setClasses(data);
      setTotalRecords(total);
    } catch (err) {
      console.error('Failed to load classes', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Unable to load classes. Please try again later.' });
      setClasses([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // when row expanded, load sections for that class (if not already loaded)
  const onRowToggle = async (e) => {
    const newRows = e.data;
    setExpandedRows(newRows);

    // get the newly expanded id(s)
    const keys = Object.keys(newRows || {});
    if (keys.length === 0) return;
    for (const id of keys) {
      const cls = classes.find((c) => String(c.id) === String(id));
      if (!cls) continue;
      if (Array.isArray(cls.sections) && cls.sections.length > 0) continue; // already loaded

      try {
        setLoading(true);
        const res = await classesAPI.getsectionsbyclass(cls.id);
        const sections = res?.data?.data || [];
        setClasses((prev) => prev.map((item) => (String(item.id) === String(cls.id) ? { ...item, sections } : item)));
      } catch (err) {
        console.error('Failed to load sections', err);
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load sections' });
      } finally {
        setLoading(false);
      }
    }
  };

  const onPage = (event) => {
    setLazyParams((prev) => ({ ...prev, first: event.first, rows: event.rows, page: Math.floor(event.first / event.rows) + 1 }));
  };

  const onFilter = (event) => {
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1, filters: event.filters }));
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value || '';
    setGlobalFilterValue(value);
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1, filters: { ...prev.filters, global: { value, matchMode: FilterMatchMode.CONTAINS } } }));
  };

  const onClassFilterChange = (e) => {
    const value = e.value || null;
    setSelectedClassFilter(value);
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
  };

  // create / edit
  const openCreateDialog = () => {
    setEditingClass(null);
    setClassForm({ name: '', description: '' });
    setSelectedSubjectIds([]);
    setDialogClassNameValue('');
    fetchAllSubjects();
    fetchCommonClasses();
    setClassDialogVisible(true);
  };

  const openEditDialog = (cls) => {
    setEditingClass(cls);
    setClassForm({ name: cls.name || '', description: cls.description || '', academicYear: cls.academicYear || new Date().getFullYear(), isActive: cls.isActive !== false });
    // preselect subjects for this class
    fetchAllSubjects();
    fetchCommonClasses();
    setDialogClassNameValue(cls.name || '');
    setClassDialogVisible(true);
  };

  const saveClass = async () => {
    // For this task: do not call API. Instead, console.log the class data (name and selected subject ids).
    try {
      const payload = {
        name: dialogClassNameValue || classForm.name,
        description: classForm.description,
        subjects: selectedSubjectIds
      };

      console.log('Class data to save:', payload);
      const res = await classesAPI.registerClass(payload);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: res?.data?.message });
      setClassDialogVisible(false);
      loadClasses();
    } catch (err) {
      console.error('Failed to save class', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to save class' });
    }

  }


  const deleteClass = (cls) => {
    confirmDialog({
      message: `Are you sure you want to Delete class "${cls.name}" regarding sections associated with it also deletes?`,
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          console.log('Deleting class id (sent to backend):', cls.id);
          // await classesAPI.delete(schoolId, cls.id);
          console.log('Delete API response for id', cls.id);
          toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Class deleted' });
          loadClasses();
        } catch (err) {
          console.error('Failed to delete', err);
          toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete class' });
        }
      },
      acceptClassName: 'bg-red-600 hover:bg-red-700 text-white font-semibold px-6 mx-5 py-2 rounded-lg transition-all shadow-md',
      rejectClassName: 'bg-gray-400 hover:bg-gray-500 text-white font-semibold px-6 py-2 rounded-lg transition-all shadow-md',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel'
    });
  };

  // Subjects helpers
  const fetchCommonClasses = async () => {
    try {
      const res = await classesAPI.getCommonClasses();
      const data = res?.data?.data || res?.data || [];
      console.log('Fetched common classes:', data);
      setCommonClasses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load common classes', err);
      setCommonClasses([]);
    }
  };

  const fetchAllSubjects = async () => {
    try {
      const res = await subjectsAPI.getAllSubjects();
      const data = res?.data?.data || res?.data || [];
      console.log('Fetched all subjects:', data);
      setAvailableSubjects(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load subjects', err);
      setAvailableSubjects([]);
    }
  };


  const onSubjectToggle = (e, subject) => {
    const checked = e.checked ?? e.target?.checked;
    if (checked) setSelectedSubjectIds((prev) => Array.from(new Set([...prev, subject.id])));
    else setSelectedSubjectIds((prev) => prev.filter((id) => id !== subject.id));
  };

  // Section assignment dialog handlers
  const openSectionDialog = (cls) => {
    setSelectedClassForSection(cls);
    setSelectedSectionNames([]);
    setSectionTeacherMap({});
    // setAvailableSections(cls.sections || []); // Removed: using common sections instead
    fetchCommonSections();
    fetchAllTeachers();
    setSectionDialogVisible(true);
  };

  const fetchCommonSections = async () => {
    try {
      const res = await classesAPI.getCommonSections();
      const data = res?.data?.data || res?.data || [];
      console.log('Fetched common sections:', data);
      setCommonSections(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load common sections', err);
      setCommonSections([]);
    }
  };

  const fetchAllTeachers = async () => {
    try {
      const res = await employeesAPI.getTeachersForClassSection();
      const data = res?.data?.data || res?.data || [];
      console.log('Fetched all teachers:', data);
      setAvailableTeachers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load teachers', err);
      setAvailableTeachers([]);
    }
  };

  const onSectionToggle = (e, sectionName) => {
    const checked = e.checked ?? e.target?.checked;
    if (checked) setSelectedSectionNames((prev) => Array.from(new Set([...prev, sectionName])));
    else {
      setSelectedSectionNames((prev) => prev.filter((name) => name !== sectionName));
      setSectionTeacherMap((prev) => {
        const updated = { ...prev };
        delete updated[sectionName];
        return updated;
      });
    }
  };

  const onSectionTeacherChange = (sectionName, teacherId) => {
    setSectionTeacherMap((prev) => ({
      ...prev,
      [sectionName]: teacherId
    }));
  };

  const assignSection = async () => {
    if (!selectedClassForSection || selectedSectionNames.length === 0) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please select at least one section' });
      return;
    }

    try {
      const payload = {
        classId: selectedClassForSection.id,
        sections: selectedSectionNames.map((sectionName) => ({
          name: sectionName,
          teacherId: sectionTeacherMap[sectionName] || null
        }))
      };

      console.log('Payload for assigning sections:', payload);
      const res = await classesAPI.addSections(payload);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: res?.data?.message || 'Sections assigned successfully' });
      setSectionDialogVisible(false);
      loadClasses();
    } catch (err) {
      console.error('Failed to assign section', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to assign section' });
    }
  };

  // view detail functions removed — list-only UI

  const classNameOptions = React.useMemo(() => (Array.isArray(commonClasses) ? commonClasses.map((c) => ({ label: c.name || c.className || `Class ${c.id}`, value: c.name || c.className || `Class ${c.id}` })) : []), [commonClasses]);

  return (
    <div style={{ height: 'calc(100vh - 64px)' }} className="flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 p-6">
      <Toast ref={toast} />
      {/* Ensure checkbox boxes have visible border when unchecked */}
      <style>{`
        .p-checkbox .p-checkbox-box{border:1px solid #cbd5e1;box-shadow:none}
        .p-checkbox .p-checkbox-box.p-highlight{border-color:#3b82f6;background:linear-gradient(#3b82f6,#2563eb)}
        .p-checkbox .p-checkbox-box:focus{box-shadow:0 0 0 4px rgba(59,130,246,0.08)}
        .p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          color: white !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }
        .p-paginator .p-paginator-pages .p-paginator-page:not(.p-highlight):hover {
          background: rgba(102, 126, 234, 0.1) !important;
          border-radius: 8px !important;
        }
      `}</style>
      <ConfirmDialog />
      
      <ClassFilter 
        user={user}
        openCreateDialog={openCreateDialog}
        globalFilterValue={globalFilterValue}
        onGlobalFilterChange={onGlobalFilterChange}
      />

      {/* Classes Directory (main card, DataTable) */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl overflow-hidden flex flex-col flex-1 min-h-0">
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                </div>
                <div className="text-center">
                  <p className="text-lg font-medium text-gray-700">Loading classes...</p>
                  <p className="text-sm text-gray-500">Please wait while we fetch the data</p>
                </div>
              </div>
            </div>
          ) : (
      <ClassesTable 
        classes={classes}
        lazyParams={lazyParams}
        totalRecords={totalRecords}
        loading={loading}
        onPage={onPage}
        onFilter={onFilter}
        expandedRows={expandedRows}
        onRowToggle={onRowToggle}
        openEditDialog={openEditDialog}
        deleteClass={deleteClass}
        user={user}
        openSectionDialog={openSectionDialog}
      />
          )}
        </div>
      </div>
      <ClassDialog 
        visible={classDialogVisible}
        onHide={() => setClassDialogVisible(false)}
        editingClass={editingClass}
        classForm={classForm}
        setClassForm={setClassForm}
        classNameOptions={classNameOptions}
        dialogClassNameValue={dialogClassNameValue}
        setDialogClassNameValue={setDialogClassNameValue}
        availableSubjects={availableSubjects}
        selectedSubjectIds={selectedSubjectIds}
        onSubjectToggle={onSubjectToggle}
        saveClass={saveClass}
      />

      <SectionDialog 
        visible={sectionDialogVisible}
        onHide={() => setSectionDialogVisible(false)}
        selectedClassForSection={selectedClassForSection}
        classes={classes}
        commonSections={commonSections}
        selectedSectionNames={selectedSectionNames}
        onSectionToggle={onSectionToggle}
        sectionTeacherMap={sectionTeacherMap}
        availableTeachers={availableTeachers}
        onSectionTeacherChange={onSectionTeacherChange}
        assignSection={assignSection}
      />
    </div>
  );
};

export default Classes;
